import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type {
  ApplyKit,
  ApplyKitField,
  ApplyKitProvenance,
  ReviewQueueClosed,
  ReviewQueueItem,
} from "@resume-cloudflare/shared";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Handshake,
  ExternalLink,
  FileText,
  MessageSquare,
  Pencil,
  ShieldAlert,
  Undo2,
  XCircle,
} from "lucide-react";
import { useApplyKit } from "../lib/apply";
import { useAddNote, useUpdateApplicationStatus } from "../lib/applications";
import {
  useApprovalDiff,
  useApprovePacket,
  useReviewQueue,
  useRevokeApproval,
  useSetFieldValue,
} from "../lib/review";
import { Button } from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";
import { Textarea } from "../components/ui/Textarea";

// /review — the approval gate (docs/SUBMISSION_FLOW_SPRINTS.md PR B).
//
// You review the exact packet that will be submitted, and approving freezes
// it by hash. Reading effort goes where a mistake matters: blockers, legal /
// eligibility answers, and prose drafted for this application are expanded;
// values reused from your profile and answer banks are collapsed. Submission
// is a separate, later step that can only use what you approved.

// ── List ──────────────────────────────────────────────────────────────────

function ageLabel(ts: number): string {
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function QueueRow({ item }: { item: ReviewQueueItem }) {
  const chips: ReactNode[] = [];
  if (item.expected_fit != null)
    chips.push(
      <Pill key="f" tone={item.expected_fit >= 2.5 ? "success" : "neutral"}>
        Fit {item.expected_fit.toFixed(1)}/4
      </Pill>,
    );
  if (item.autofill) chips.push(<Pill key="a" tone="info">Auto-fill</Pill>);
  if (!item.extracted) chips.push(<Pill key="x">Form not extracted</Pill>);
  if (item.counts.blockers > 0)
    chips.push(
      <Pill key="b" tone="danger">
        {item.counts.blockers} missing
      </Pill>,
    );
  if (item.stale_documents > 0)
    chips.push(
      <Pill key="d" tone="danger">
        {item.stale_documents} stale document{item.stale_documents === 1 ? "" : "s"}
      </Pill>,
    );
  if (item.approval?.stale)
    chips.push(
      <Pill key="s" tone="warning">
        Changed since approval
      </Pill>,
    );
  if (item.counts.review > 0)
    chips.push(
      <Pill key="r" tone="accent">
        {item.counts.review} to read
      </Pill>,
    );
  if (item.counts.risk > 0)
    chips.push(
      <Pill key="k" tone="warning">
        {item.counts.risk} to confirm
      </Pill>,
    );
  if (!item.has_resume_version) chips.push(<Pill key="d">No tailored resume</Pill>);
  // Optional chaining: a queue payload cached from before PR D has no warm_path.
  if (item.warm_path?.recent) {
    chips.push(
      <Pill key="w" tone="success">
        Warm path
      </Pill>,
    );
  } else if (item.warm_path?.company_tier === 1) {
    chips.push(
      <Pill key="w" tone="info">
        No warm path yet
      </Pill>,
    );
  }

  return (
    <li>
      <Link
        to={`/review/${item.application_id}`}
        className="flex items-center justify-between gap-3 rounded-md px-2 py-2.5 hover:bg-neutral-900"
      >
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm text-neutral-100">
            {item.role} <span className="text-neutral-500">· {item.company_name}</span>
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {chips}
            <span className="text-xs text-neutral-500">waiting {ageLabel(item.updated_at)}</span>
          </div>
        </div>
        <ChevronRight size={16} aria-hidden className="shrink-0 text-neutral-600" />
      </Link>
    </li>
  );
}

function QueueGroup({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: ReviewQueueItem[];
}) {
  if (items.length === 0) return null;
  return (
    <Card>
      <CardHeader title={`${title} (${items.length})`} description={description} />
      <CardBody>
        <ul className="-mx-2 divide-y divide-neutral-800/70">
          {items.map((i) => (
            <QueueRow key={i.application_id} item={i} />
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function ClosedGroup({ items }: { items: ReviewQueueClosed[] }) {
  if (items.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title={`Listing closed (${items.length})`}
        description="These postings are gone. Open one and choose Not this one to withdraw it."
      />
      <CardBody>
        <ul className="-mx-2 divide-y divide-neutral-800/70">
          {items.map((i) => (
            <li key={i.application_id}>
              <Link
                to={`/review/${i.application_id}`}
                className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-neutral-900"
              >
                <p className="min-w-0 truncate text-sm text-neutral-400">
                  {i.role} <span className="text-neutral-600">· {i.company_name}</span>
                </p>
                <span className="shrink-0 text-xs text-neutral-600">closed {ageLabel(i.closed_at)} ago</span>
              </Link>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

export function ReviewList() {
  const queue = useReviewQueue();
  if (queue.isLoading) {
    return (
      <section className="mx-auto max-w-3xl space-y-3 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </section>
    );
  }
  const items = queue.data?.items ?? [];
  const closed = queue.data?.closed ?? [];
  const liveTotal = queue.data?.live_total ?? items.length;
  const approved = items.filter((i) => i.approval && !i.approval.stale);
  const ready = items.filter(
    (i) =>
      (!i.approval || i.approval.stale) &&
      i.extracted &&
      i.counts.blockers === 0 &&
      i.stale_documents === 0,
  );
  const drafting = items.filter(
    (i) =>
      (!i.approval || i.approval.stale) &&
      (!i.extracted || i.counts.blockers > 0 || i.stale_documents > 0),
  );

  return (
    <section className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6 lg:p-8">
      <header>
        <h1 className="text-lg font-semibold text-neutral-100">Review</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Approve the exact packet before anything is submitted. Approving doesn&rsquo;t send
          anything. It locks in what you read. Best fit first.
        </p>
        {liveTotal > items.length ? (
          <p className="mt-1 text-xs text-neutral-500">
            Showing the top {items.length} of {liveTotal} open packets.
          </p>
        ) : null}
      </header>
      {items.length === 0 && closed.length === 0 ? (
        <EmptyState
          title="Nothing waiting for review"
          description="Packets appear here once Claude has drafted them."
        />
      ) : null}
      <QueueGroup
        title="Approved: ready to submit"
        description="Submit these next. Ask Claude to fill one in your browser; you click Submit."
        items={approved}
      />
      <QueueGroup
        title="Ready for your review"
        description="Every required field has a value. Read, edit, approve."
        items={ready}
      />
      <QueueGroup
        title="Still being prepared"
        description="Missing required answers. Ask Claude to finish these."
        items={drafting}
      />
      <ClosedGroup items={closed} />
    </section>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────

const PROVENANCE_LABEL: Record<ApplyKitProvenance, string> = {
  answer: "Written for this application",
  profile: "From your profile",
  standing_answer: "Your standing answer",
  company_answer: "Your saved answer for this company",
  none: "",
};

function FieldValue({ field }: { field: ApplyKitField }) {
  if (field.value === null) {
    return <p className="text-xs text-neutral-500">No value yet.</p>;
  }
  return (
    <div className="space-y-1">
      <p className="whitespace-pre-wrap break-words rounded bg-neutral-900 px-3 py-2 text-sm leading-relaxed text-neutral-100">
        {field.value}
      </p>
      {field.options && field.options.length > 0 ? (
        <p className="text-xs text-neutral-500">Options: {field.options.join(" · ")}</p>
      ) : null}
    </div>
  );
}

function EditField({ applicationId, field }: { applicationId: string; field: ApplyKitField }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(field.value ?? "");
  const save = useSetFieldValue(applicationId);
  const hasOptions = !!field.options && field.options.length > 0;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        leadingIcon={<Pencil size={12} aria-hidden />}
        onClick={() => {
          setDraft(field.value ?? "");
          setOpen(true);
        }}
      >
        Edit
      </Button>
      <Dialog.Content
        className={hasOptions ? undefined : "w-[min(92vw,44rem)]"}
        title={field.label}
        description="This changes the answer for this application only. Your profile and saved answers stay as they are."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!draft.trim() || save.isPending}
              onClick={async () => {
                await save.mutateAsync({ field_name: field.name, answer: draft });
                setOpen(false);
              }}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        {hasOptions ? (
          <div className="flex flex-wrap gap-2">
            {field.options!.map((o) => (
              <Button
                key={o}
                size="sm"
                variant={draft === o ? "primary" : "secondary"}
                onClick={() => setDraft(o)}
              >
                {o}
              </Button>
            ))}
          </div>
        ) : (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={field.kind === "essay" || field.kind === "experience" ? 12 : 3}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function FieldCard({
  applicationId,
  field,
  editable,
}: {
  applicationId: string;
  field: ApplyKitField;
  editable: boolean;
}) {
  const words = field.value ? field.value.trim().split(/\s+/).length : 0;
  return (
    <div className="space-y-2 border-t border-neutral-800 py-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-200">
            {field.label}
            {field.required ? <span className="ml-1 text-danger-fg">*</span> : null}
          </p>
          <p className="text-xs text-neutral-500">
            {PROVENANCE_LABEL[field.provenance]}
            {field.kind === "essay" || field.kind === "experience" ? ` · ${words} words` : ""}
          </p>
          {field.help_text ? (
            <p className="mt-1 text-xs text-neutral-500">{field.help_text}</p>
          ) : null}
          {field.audit_issues?.map((issue) => (
            <p key={issue} className="mt-1 text-xs text-danger-fg">
              {issue}
            </p>
          ))}
        </div>
        {editable ? <EditField applicationId={applicationId} field={field} /> : null}
      </div>
      <FieldValue field={field} />
    </div>
  );
}

function StatusBanner({ kit }: { kit: ApplyKit }) {
  const revoke = useRevokeApproval(kit.application.id);
  const approval = kit.approval && kit.approval.submitted_at === null ? kit.approval : null;
  const diff = useApprovalDiff(kit.application.id, !!approval?.stale);

  if (kit.approval?.submitted_at) {
    return (
      <Card className="border-success-border">
        <CardBody className="flex items-center gap-2 text-sm text-success-fg">
          <CheckCircle2 size={16} aria-hidden />
          Submitted {new Date(kit.approval.submitted_at).toLocaleDateString()}.
        </CardBody>
      </Card>
    );
  }
  if (approval && !approval.stale) {
    return (
      <Card className="border-success-border">
        <CardBody className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-medium text-success-fg">
            <CheckCircle2 size={16} aria-hidden />
            Approved {new Date(approval.approved_at).toLocaleString()}
          </p>
          <p className="text-sm text-neutral-300">
            Next: ask Claude to <em>fill application {kit.application.id.slice(0, 8)}</em>. It
            fills the form in your browser from exactly this packet, checks every field, and stops.
            You click Submit. Or paste it yourself from the{" "}
            <Link
              to={`/applications/${kit.application.id}/apply-kit`}
              className="underline underline-offset-4"
            >
              apply kit
            </Link>
            .
          </p>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Undo2 size={12} aria-hidden />}
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
          >
            Withdraw approval
          </Button>
        </CardBody>
      </Card>
    );
  }
  if (approval?.stale) {
    return (
      <Card className="border-warning-border">
        <CardBody className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-medium text-warning-fg">
            <AlertTriangle size={16} aria-hidden />
            Changed since you approved it. Approve again to submit.
          </p>
          {diff.data ? (
            <ul className="space-y-2 text-xs">
              {diff.data.fields.map((f) => (
                <li key={f.name}>
                  <p className="text-neutral-300">{f.label}</p>
                  <p className="text-danger-fg line-through">{f.approved ?? "(empty)"}</p>
                  <p className="text-success-fg">{f.current ?? "(empty)"}</p>
                </li>
              ))}
              {diff.data.documents_changed ? (
                <li className="text-neutral-300">The attached resume or cover letter changed.</li>
              ) : null}
            </ul>
          ) : null}
        </CardBody>
      </Card>
    );
  }
  return null;
}

function Documents({ kit }: { kit: ApplyKit }) {
  const resume = kit.documents.resume_versions[0];
  const letter = kit.documents.cover_letters[0];
  const [preview, setPreview] = useState<string | null>(null);
  const rows = [
    { kind: "Resume", doc: resume },
    { kind: "Cover letter", doc: letter },
  ];
  return (
    <Card>
      <CardHeader
        title="Attachments"
        description="The newest tailored resume and cover letter are what gets uploaded."
      />
      <CardBody className="space-y-3">
        <ul className="space-y-2">
          {rows.map(({ kind, doc }) => (
            <li key={kind} className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2 text-sm text-neutral-200">
                <FileText size={14} aria-hidden className="shrink-0 text-neutral-500" />
                <span className="truncate">
                  {kind}: {doc ? (doc.label ?? "latest") : <span className="text-neutral-500">none</span>}
                </span>
              </span>
              {doc ? (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreview(preview === doc.pdf_url ? null : doc.pdf_url)}
                  >
                    {preview === doc.pdf_url ? "Hide" : "Preview"}
                  </Button>
                  <a href={doc.pdf_url} target="_blank" rel="noreferrer">
                    <Button variant="ghost" size="sm" leadingIcon={<ExternalLink size={12} aria-hidden />}>
                      Open
                    </Button>
                  </a>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {preview ? (
          <iframe
            title="Document preview"
            src={preview}
            className="h-[70vh] w-full rounded border border-neutral-800 bg-white"
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

function daysAgo(ts: number): string {
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

// Warm path (SUBMISSION_FLOW_SPRINTS PR D). Not part of what you approve:
// naming or messaging someone never makes an approval stale. On a Tier-1
// packet with no recent touch this is a soft nudge, never a blocker.
function WarmPathCard({ kit }: { kit: ApplyKit }) {
  const wp = kit.warm_path;
  if (!wp) return null;
  const tier1 = wp.company_tier === 1;
  if (wp.contacts.length === 0 && !tier1) return null;
  return (
    <Card className={!wp.recent && tier1 ? "border-info-border" : undefined}>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Handshake size={14} aria-hidden className="text-neutral-400" />
            Warm path
          </span>
        }
        description={
          wp.recent
            ? `You last reached someone here ${daysAgo(wp.last_touch!)}.`
            : wp.contacts.length === 0
              ? "No one named at this company yet. A cold application alone rarely gets a response at this tier. Name one person to Claude (a hiring manager, a solutions architect on the team, a former colleague) and it drafts the note. You send it."
              : "No message in the last 30 days. A short note to one of these people alongside the application helps it get read."
        }
      />
      {wp.contacts.length > 0 ? (
        <CardBody>
          <ul className="space-y-1.5">
            {wp.contacts.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate text-neutral-200">
                  {c.linkedin_url ? (
                    <a
                      href={c.linkedin_url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline-offset-4 hover:underline"
                    >
                      {c.full_name}
                    </a>
                  ) : (
                    c.full_name
                  )}
                  {c.role ? <span className="text-neutral-500"> · {c.role}</span> : null}
                </span>
                <span className="shrink-0 text-xs text-neutral-500">
                  {c.last_contacted_at ? daysAgo(c.last_contacted_at) : "not contacted"}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/contacts" className="mt-2 inline-block text-xs text-neutral-400 underline underline-offset-4">
            Contacts
          </Link>
        </CardBody>
      ) : null}
    </Card>
  );
}

function SendBack({ applicationId }: { applicationId: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const addNote = useAddNote(applicationId);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<MessageSquare size={12} aria-hidden />}
        onClick={() => setOpen(true)}
      >
        Send back to Claude
      </Button>
      <Dialog.Content
        title="What should change?"
        description="Saved as a note on the application. Claude reads it next time you work on this packet."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!text.trim() || addNote.isPending}
              onClick={async () => {
                await addNote.mutateAsync({ text: `Review feedback: ${text.trim()}` });
                setText("");
                setOpen(false);
              }}
            >
              Save note
            </Button>
          </>
        }
      >
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="e.g. The essay should open with the platform migration, and keep it under 300 words."
        />
      </Dialog.Content>
    </Dialog>
  );
}

function NotThisOne({ applicationId }: { applicationId: string }) {
  const [open, setOpen] = useState(false);
  const update = useUpdateApplicationStatus();
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        leadingIcon={<XCircle size={12} aria-hidden />}
        onClick={() => setOpen(true)}
      >
        Not this one
      </Button>
      <Dialog.Content
        title="Skip this application?"
        description="Moves it to withdrawn. It leaves the review queue. You can move it back from Applications."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={update.isPending}
              onClick={async () => {
                await update.mutateAsync({
                  id: applicationId,
                  status: "withdrawn",
                  reason: "skipped in review",
                });
                navigate("/review");
              }}
            >
              Skip it
            </Button>
          </>
        }
      >
        <span />
      </Dialog.Content>
    </Dialog>
  );
}

export function ReviewDetail() {
  const { applicationId = "" } = useParams<{ applicationId: string }>();
  const kitQuery = useApplyKit(applicationId);
  const approve = useApprovePacket(applicationId);
  const kit = kitQuery.data;

  if (kitQuery.isLoading) {
    return (
      <section className="mx-auto max-w-3xl space-y-3 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </section>
    );
  }
  if (kitQuery.error || !kit) {
    return (
      <section className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
        <EmptyState tone="danger" title="Couldn't load this packet" />
      </section>
    );
  }

  const byName = new Map(kit.fields.map((f) => [f.name, f]));
  const inFormOrder = kit.sections.flatMap((s) =>
    s.field_names.map((n) => byName.get(n)).filter((f): f is ApplyKitField => !!f),
  );
  const blockers = inFormOrder.filter((f) => f.attention === "blocker");
  const toRead = inFormOrder.filter((f) => f.attention === "review");
  const toConfirm = inFormOrder.filter((f) => f.attention === "risk");
  const manual = inFormOrder.filter((f) => f.status === "manual" && f.attention === "none");
  const reused = inFormOrder.filter(
    (f) => f.attention === "none" && f.status !== "manual" && f.value !== null,
  );
  const liveApproval =
    kit.approval && !kit.approval.stale && kit.approval.submitted_at === null;
  const submitted = !!kit.approval?.submitted_at;
  const staleDocs = kit.document_audit.filter((d) => !d.ok);
  const canApprove =
    !!kit.extraction &&
    blockers.length === 0 &&
    staleDocs.length === 0 &&
    !liveApproval &&
    !submitted;
  const editable = !submitted;

  return (
    <section className="mx-auto max-w-3xl space-y-4 p-4 pb-28 sm:p-6 lg:p-8">
      <header className="space-y-1">
        <Link to="/review" className="text-xs text-neutral-500 hover:text-neutral-300">
          ← Review
        </Link>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="min-w-0 text-lg font-semibold text-neutral-100">{kit.application.role}</h1>
          <a href={kit.application.url} target="_blank" rel="noreferrer">
            <Button variant="ghost" size="sm" leadingIcon={<ExternalLink size={12} aria-hidden />}>
              Open posting
            </Button>
          </a>
        </div>
        <p className="text-xs text-neutral-500">
          {kit.fields.length} fields · {toRead.length} to read · {toConfirm.length} to confirm ·{" "}
          {reused.length} reused
        </p>
      </header>

      <StatusBanner kit={kit} />

      {!kit.extraction ? (
        <EmptyState
          title="Form not extracted yet"
          description="Ask Claude to extract this application's form first."
        />
      ) : null}

      {staleDocs.length > 0 ? (
        <Card className="border-danger-border">
          <CardHeader
            title={`Stale documents (${staleDocs.length})`}
            description="These print something your career corpus no longer supports. Ask Claude to recompose them, then review again."
          />
          <CardBody>
            {staleDocs.map((d) => (
              <div key={d.id} className="space-y-1 py-2">
                <p className="text-sm font-medium text-neutral-100">
                  {d.kind === "resume_version" ? "Tailored resume" : "Cover letter"}
                </p>
                <ul className="list-disc space-y-0.5 pl-5 text-xs text-neutral-400">
                  {d.issues.map((i, n) => (
                    <li key={n}>{i.detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </CardBody>
        </Card>
      ) : null}

      {blockers.length > 0 ? (
        <Card className="border-danger-border">
          <CardHeader
            title={`Fix first (${blockers.length})`}
            description="Required fields with no answer, and drafted answers that print a retracted or age-revealing claim. Fix them here or ask Claude."
          />
          <CardBody>
            {blockers.map((f) => (
              <FieldCard key={f.name} applicationId={applicationId} field={f} editable={editable} />
            ))}
          </CardBody>
        </Card>
      ) : null}

      {toRead.length > 0 ? (
        <Card>
          <CardHeader
            title={`Read these (${toRead.length})`}
            description="Written for this application. This is where your voice matters."
          />
          <CardBody>
            {toRead.map((f) => (
              <FieldCard key={f.name} applicationId={applicationId} field={f} editable={editable} />
            ))}
          </CardBody>
        </Card>
      ) : null}

      {toConfirm.length > 0 ? (
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <ShieldAlert size={14} aria-hidden className="text-warning-fg" />
                Confirm these ({toConfirm.length})
              </span>
            }
            description="Eligibility, legal and compensation answers. A wrong one here costs more than a typo."
          />
          <CardBody>
            {toConfirm.map((f) => (
              <FieldCard key={f.name} applicationId={applicationId} field={f} editable={editable} />
            ))}
          </CardBody>
        </Card>
      ) : null}

      <Documents kit={kit} />

      <WarmPathCard kit={kit} />

      {manual.length > 0 ? (
        <Card>
          <CardHeader
            title="Done in the browser"
            description="Uploads and checkboxes, handled when the form is filled."
          />
          <CardBody>
            <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-300">
              {manual.map((f) => (
                <li key={f.name}>{f.label}</li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {reused.length > 0 ? (
        <Card>
          <details>
            <summary className="cursor-pointer list-none px-4 py-3 text-sm text-neutral-300 hover:text-neutral-100">
              {reused.length} fields reused from your profile and saved answers ▸
            </summary>
            <CardBody className="border-t border-neutral-800/80">
              {reused.map((f) => (
                <FieldCard key={f.name} applicationId={applicationId} field={f} editable={editable} />
              ))}
            </CardBody>
          </details>
        </Card>
      ) : null}

      {!submitted ? (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-neutral-800 bg-neutral-950/95 backdrop-blur">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-4 py-3">
            <div className="flex items-center gap-1">
              <SendBack applicationId={applicationId} />
              <NotThisOne applicationId={applicationId} />
            </div>
            {liveApproval ? (
              <Pill tone="success">Approved</Pill>
            ) : (
              <Button
                variant="primary"
                size="md"
                disabled={!canApprove || approve.isPending}
                leadingIcon={<CheckCircle2 size={14} aria-hidden />}
                onClick={() => approve.mutate({ snapshot_sha256: kit.snapshot_sha256 })}
                title={blockers.length > 0 ? "Fill the missing fields first" : undefined}
              >
                {approve.isPending ? "Approving…" : "Approve this packet"}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
